// Where review left a session, as a clause of the workspace's search:
// `review:met` (a standard met one of its findings), `review:not_verified`
// (flagged, not checked yet), `review:not_met` (true, but no standard asks a
// person to look), `review:set_aside` (false, or could not be checked),
// `review:none` (nothing flagged); several at once (`review:met,not_met`); or
// left out (`-review:none`, anything flagged).
//
// The query language is compiled over requests, and exposes only the fields
// the analytics API can express (lib/queryLang.js); where review left a
// session is read in the browser from the range's findings. So `review` is
// not one of its fields: it parses as a field it does not know, which a
// session match passes over, and a search's review clauses are taken out of
// what a match is asked (`rest`) and applied to the sessions it returns
// (`keeps`). They are read at the top level of a search only: one under an
// OR stays in the search, and the match passes over it.
//
// PURE: parsing only, so it unit-tests in node.
import { parseQuery } from "./queryLang.js";

export const REVIEW_PLACES = ["met", "not_verified", "not_met", "set_aside", "none"];
const WORDS = { met: "standard met", not_verified: "not checked yet", not_met: "no standard met", set_aside: "set aside", none: "nothing flagged" };
// What a review clause's places are called on its pill.
export const reviewWords = (places) => places.map((p) => WORDS[p] || p).join(", ");
export const reviewClause = (place) => `review:${place}`;

const NONE = Object.freeze([]);
// A review clause as the parser leaves it: a field it does not know.
const RAW = /^review:([a-z_,]+)$/i;

// A search's review clauses (`tokens`: each as written, in its positive form
// (`text`), whether it leaves its places out, and the places), the search
// without them (`rest`), and whether a session review left at a place passes
// them all (`keeps(place)`); no `keeps` where the search has none.
export function splitReview(text) {
  const t = String(text ?? "");
  if (!/review\s*(:|!=)/i.test(t)) return { rest: t, tokens: NONE, keeps: null };
  const { ast } = parseQuery(t);
  const nodes = !ast ? [] : ast.type === "and" ? ast.children : [ast];
  const tokens = [];
  const cuts = [];
  for (const node of nodes) {
    const inner = node.type === "not" ? node.child : node;
    const m = inner?.type === "error" ? RAW.exec(String(inner.raw || "").trim()) : null;
    const places = m ? m[1].toLowerCase().split(",").filter((v) => REVIEW_PLACES.includes(v)) : [];
    if (!places.length || !inner.span) continue;
    let [s, e] = inner.span;
    // The brackets around it, and a NOT written before it, go with it.
    while (t[s - 1] === "(" && t[e] === ")") {
      s -= 1;
      e += 1;
    }
    let b = s;
    while (b > 0 && /\s/.test(t[b - 1])) b -= 1;
    if (b >= 3 && t.slice(b - 3, b).toUpperCase() === "NOT" && (b === 3 || /[\s(]/.test(t[b - 4]))) s = b - 3;
    const positive = `review:${places.join(",")}`;
    tokens.push({ raw: t.slice(s, e), text: positive, negated: node.type === "not", places });
    cuts.push([s, e]);
  }
  if (!tokens.length) return { rest: t, tokens: NONE, keeps: null };
  const pieces = [];
  let at = 0;
  for (const [s, e] of cuts.sort((a, b) => a[0] - b[0])) {
    pieces.push(t.slice(at, s));
    at = e;
  }
  pieces.push(t.slice(at));
  return {
    rest: pieces.map((p) => p.trim()).filter(Boolean).join(" "),
    tokens,
    keeps: (place) => tokens.every((k) => k.places.includes(place) !== k.negated),
  };
}

// Whether every session a search's review clauses keep was flagged: they are
// then the findings' own sessions, every one in the range, rather than some
// of the sessions a match lists.
export const flaggedOnly = (keeps) => !!keeps && !keeps("none");
